/* ============================================================
   Dathent — AutopilotWidget
   Visual matches design/pages/autopilot.jsx (progress track with
   phase tick marks, per-step progress bar, animated orb), but state
   comes from /api/autopilot/state (real backend), not a demo clock.

   Completion marks require current-cycle evidence. An enabled toggle or
   a recent start timestamp does not mean generation succeeded.
   ============================================================ */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Icons as I } from '../pack-icons';
import { api } from '../../services/api/client.js';
import { useDashboardData } from '../../state/DashboardDataContext.jsx';
import NoBrandGuard from '../NoBrandGuard.jsx';
import { useUpgrade } from '../../contexts/UpgradeContext.jsx';
import { useBrand } from '../../contexts/BrandContext.jsx';
import AutopilotIntroModal from '../AutopilotIntroModal.jsx';
import { trialingAutopilotPlan } from '../../lib/autopilotTrial.js';
import { canReadEntitledFeature } from '../../lib/readAccess.js';
import { autopilotActivityStatus, autopilotPlanStatus, currentPlanCycle } from '../../lib/autopilotRunStatus.js';

/* Phase manifest. Matches dathent-autopilot-worker:
     PHASE_BY_DOW[0] = 'plan'    (Sunday)
     PHASE_BY_DOW[5] = 'review'  (Friday)
     PHASE_BY_DOW[6] = 'tune'    (Saturday)
   `phase` is the normalized position [0..1) on the weekly track — used
   to place the tick marks. The active task comes from backend evidence. */
// NOTE: there is no separate "compose visuals" step. All generation —
// copy AND images/carousels — happens inside the Sunday PLAN run. A standalone
// "Compose visuals" task used to own a big mid-week slice of the track, so on a
// Tuesday the widget falsely spotlighted "composing visuals" when nothing was
// generating. The honest mid-week state is PUBLISH: the week's posts go out on
// their scheduled slots while autopilot stands by until Friday's review. Phase
// values are normalized positions [0..1) on the Sun→Sat track (Sun=0).
// All five phases are live now (review/tune ship the weekly learning loop).
// The `soon` flag is kept supported in the render guards but no phase sets it.
const AP_TASKS = [
  { id: 'plan',    label: 'Plan & generate',      detail: "Generate next week's posts + visuals", icon: 'sparkles', day: 'Sun',     dur: '~3 min',   phase: 0.04 },
  { id: 'publish', label: 'Publish & cross-post', detail: 'Daily slots across channels',          icon: 'send',     day: 'Mon–Thu', dur: 'all week', phase: 0.16 },
  { id: 'review',  label: 'Review performance',   detail: 'Analyze last week',                    icon: 'trend',    day: 'Fri',     dur: '~1 min',   phase: 0.70 },
  { id: 'tune',    label: 'Tune content plan',    detail: 'Rebalance pillars',                    icon: 'target',   day: 'Sat',     dur: '~30 s',    phase: 0.85 },
  { id: 'rest',    label: 'Standing by',          detail: 'Monitoring until Sunday',              icon: 'clock',    day: '·',       dur: 'idle',     phase: 0.95 },
];

// Older admission receipts say "Generated N" before generation runs. History
// can acknowledge that request without inventing a completed batch outcome.
function localizeRunSummary(summary, t) {
  const gen = typeof summary === 'string' && summary.match(/^Generated (\d+) posts? for the week$/i);
  if (gen) return t('autopilotWidget.runStatus.requested', { count: Number(gen[1]) });
  return t('autopilotWidget.runStatus.accepted');
}

// The next generation is the upcoming Sunday PLAN. Returns that moment as ISO so the footer can show a
// "next batch in N days" countdown that maps to when new posts actually appear.
function nextPlanIsoFrom(now) {
  const d = new Date(now);
  d.setHours(9, 0, 0, 0);
  let add = (7 - now.getDay()) % 7; // days until the next Sunday (0 if today is Sunday)
  // Sunday BEFORE 09:00 → generation happens TODAY; only after 09:00 does
  // "next batch" mean next week's Sunday. (Used to always jump a week, so
  // Sunday-morning dashboards claimed "in 7 d" an hour before posts landed.)
  if (add === 0 && now.getHours() >= 9) add = 7;
  d.setDate(d.getDate() + add);
  return d.toISOString();
}

// Feed rows get a fuller stamp: "Sun, Jun 7 · 09:00".
function formatRunDate(iso, locale) {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  try {
    return new Intl.DateTimeFormat(locale, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(ms));
  } catch {
    return new Date(ms).toLocaleString();
  }
}

// "Sun, Jun 28" — the DATE of the next weekly generation. Clearer than a
// rolling "in N days" countdown, which jumps 1d→7d every Sunday and gave no
// hint that publishing then happens across the week (the Mon→Thu slots).
function formatPlanDate(iso, locale, timeZone) {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  // Render in the autopilot's OWN timezone (the plan is "Sunday 09:00 local"),
  // not the browser's — otherwise a browser in another zone shifts the weekday
  // and date (e.g. shows "Sat Jun 27" for a Kyiv "Sun Jun 28" plan).
  const base = { weekday: 'short', month: 'short', day: 'numeric' };
  try {
    return new Intl.DateTimeFormat(locale, timeZone ? { ...base, timeZone } : base).format(new Date(ms));
  } catch {
    try { return new Intl.DateTimeFormat(locale, base).format(new Date(ms)); } catch { return null; }
  }
}

const PLATFORM_LABEL = {
  instagram: 'Instagram', facebook: 'Facebook', telegram: 'Telegram', threads: 'Threads',
  tiktok: 'TikTok', x: 'X', linkedin: 'LinkedIn', youtube: 'YouTube',
};
function platformLabel(p) {
  const k = String(p || '').toLowerCase();
  return PLATFORM_LABEL[k] || (k ? k.charAt(0).toUpperCase() + k.slice(1) : '');
}

// Real publish outcome of the last weekly cycle → a compact honest line, e.g.
// "2 published · 1 failed". Backed by state.lastCycle (the worker
// joins the batch through scheduled_posts + publish_targets), NOT the optimistic
// "Generated N posts" batch-creation count that hid every downstream failure.
function cycleSummaryText(lc, t) {
  if (!lc) return null;
  const parts = [];
  if (lc.published > 0) parts.push(t('autopilotWidget.cycle.published', { count: lc.published }));
  if (lc.scheduled > 0) parts.push(t('autopilotWidget.cycle.queued', { count: lc.scheduled }));
  if (lc.failed > 0) parts.push(t('autopilotWidget.cycle.failed', { count: lc.failed }));
  return parts.length ? parts.join(' · ') : null;
}

function activeTaskIndex(state, planStatus) {
  if (['running', 'preparing', 'generating', 'retrying', 'reconciling', 'failed', 'skipped', 'unknown'].includes(planStatus)) return 0;
  const phase = state?.currentPhase;
  if (phase) {
    // When a phase is actively running, spotlight the matching task.
    if (phase === 'plan') return 0;
    if (phase === 'review') return 2;
    if (phase === 'tune') return 3;
    if (phase === 'rest') return 4;
  }
  return currentPlanCycle(state)?.scheduled > 0 ? 1 : 4;
}

function formatCountdown(nextRunAt, t) {
  if (!nextRunAt) return null;
  const ms = new Date(nextRunAt).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return t('autopilotWidget.startingSoon');
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return t('autopilotWidget.inMinutes', { minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('autopilotWidget.inHours', { hours });
  const days = Math.round(hours / 24);
  return t('autopilotWidget.inDays', { days });
}

/* Ambient animation: a 0→1 cycle every 14s that paints the per-step
   progress bar inside the "now" task. We only run rAF while the widget
   is enabled and is in a "running" state to avoid burning CPU on idle
   dashboards. */
function useAmbientClock(active) {
  const [t, setT] = useState(0);
  const rafRef = useRef(null);
  useEffect(() => {
    if (!active) {
      setT(0);
      return undefined;
    }
    const start = performance.now();
    const tick = (now) => {
      const elapsed = (now - start) / 14000;
      setT(elapsed % 1);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [active]);
  return t;
}

const COLLAPSED_STORAGE_KEY = 'dathent.autopilot.collapsed';

function readCollapsedPref() {
  if (typeof window === 'undefined') return true;
  try {
    const stored = window.localStorage.getItem(COLLAPSED_STORAGE_KEY);
    // Default to COLLAPSED so the widget reads as a compact glance-card
    // most of the time. Only return false if the user explicitly
    // expanded it before.
    return stored !== 'false';
  } catch {
    return true;
  }
}
function writeCollapsedPref(collapsed) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(COLLAPSED_STORAGE_KEY, collapsed ? 'true' : 'false');
  } catch {
    /* localStorage may be disabled — ignore */
  }
}

// After enabling, autopilot immediately kicks off its first cycle. Lock the
// toggle briefly afterwards so it can't be flipped on→off→on in quick
// succession — confusing, and it fights the in-flight generation. (The server's
// single-flight lock already prevents a real double-run; this is the UX guard.)
// Enabling, which starts generation, gets a longer settle window than disabling.
const ENABLE_COOLDOWN_MS = 30_000;
const DISABLE_COOLDOWN_MS = 8_000;

export default function AutopilotWidget({ alwaysExpanded = false } = {}) {
  const { t, i18n } = useTranslation();
  // Autopilot is a Brand Pilot+ feature. Lower plans get a locked card with an
  // upgrade path (the backend also 403s a direct enable). `entitlements` is null
  // until loaded — treat that as "not yet known" to avoid a locked→unlocked flash.
  const { entitlements, setupStatus } = useDashboardData?.() || {};
  const { openUpgrade } = useUpgrade();
  const brand = useBrand();
  // TASK-331 (D6 fix): a lapsed org with subscription history that had
  // autopilot configured before must still SEE its state/schedule/history —
  // the locked-upsell wall below is a create/run wall (enabling autopilot
  // stays correctly blocked server-side with PLAN_LAPSED), not a read wall.
  const autopilotEntitled = canReadEntitledFeature(entitlements, 'autopilot');
  const entitlementsLoaded = entitlements != null;
  const awaitingFirstPayment = trialingAutopilotPlan(entitlements);
  const navigate = useNavigate();
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [state, setState] = useState(null);
  const [connectedCount, setConnectedCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Timestamp until which the toggle stays locked after a change (see cooldown
  // constants above). `tick` forces a re-render the instant it elapses so the
  // button re-enables on its own, without waiting for the next 60s state poll.
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [, setTick] = useState(0);
  // Collapsed by default — the widget shows just header + timeline track
  // with hoverable dots. Users who want the full strip + footer can
  // expand via the header chevron; preference is persisted across reloads.
  const [collapsed, setCollapsed] = useState(readCollapsedPref);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setState(null);
    setConnectedCount(null);
    // Locked plans must not poll a feature they cannot use. Keep the existing
    // read-only view for lapsed subscribers via canReadEntitledFeature.
    if (!entitlementsLoaded || !autopilotEntitled || awaitingFirstPayment) {
      setLoading(false);
      return undefined;
    }
    const fetchState = async () => {
      try {
        const [res, accountsRes] = await Promise.all([
          api.autopilot.getState(),
          api.socialAccounts.list(),
        ]);
        if (cancelled) return;
        const payload = res?.data?.state ?? res?.state ?? null;
        const accountsPayload = accountsRes?.data && typeof accountsRes.data === 'object'
          ? accountsRes.data
          : accountsRes;
        const accounts = accountsPayload?.accounts || accountsPayload?.socialAccounts || [];
        setState(payload);
        setConnectedCount(accounts.filter((account) => ![
          'needs_reconnect', 'expired', 'failed', 'revoked', 'disconnected', 'error',
        ].includes(String(account?.status || '').toLowerCase())).length);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || t('autopilotWidget.failedToLoad'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchState();
    // 60s refresh — backend phases are cron-driven and slow (~15 min
    // cadence), so polling more often than this would be wasted work.
    const interval = setInterval(fetchState, 60_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [brand.activeBrandId, entitlementsLoaded, autopilotEntitled, awaitingFirstPayment]);

  // Re-render exactly when the toggle cooldown elapses so the button flips back
  // to enabled on its own.
  useEffect(() => {
    const remaining = cooldownUntil - Date.now();
    if (remaining <= 0) return undefined;
    const id = setTimeout(() => setTick((n) => n + 1), remaining + 50);
    return () => clearTimeout(id);
  }, [cooldownUntil]);

  const enabled = !!state?.enabled;
  const planStatus = autopilotPlanStatus(state, { unavailable: !!error });
  const generationComplete = planStatus === 'ready';
  const phaseRunning = !error && !['failed', 'skipped', 'unknown', 'preparing', 'retrying', 'reconciling'].includes(planStatus)
    && !!state?.currentPhase && state.currentPhase !== 'rest';
  const activeIdx = activeTaskIndex(state, planStatus);
  const task = AP_TASKS[activeIdx];
  const activelyWorking = enabled && (phaseRunning || ['running', 'generating'].includes(planStatus));
  const ambientT = useAmbientClock(activelyWorking);
  const latestPlan = state?.lastPlanRun;
  const latestPlanMoment = formatRunDate(latestPlan?.startedAt, i18n.language);
  const lastPlanMs = Date.parse(latestPlan?.startedAt);
  const freshRun = generationComplete && Number.isFinite(lastPlanMs) && Date.now() - lastPlanMs < 48 * 3600_000;

  // Recent runs feed — fetched when the user opens the footer activity
  // toggle. Plan runs + failures only; succeeded stub phases are noise.
  // Keep stranded running rows visible as unconfirmed, not as success.
  const [runsOpen, setRunsOpen] = useState(false);
  const [runs, setRuns] = useState(null);
  useEffect(() => {
    if (!runsOpen || !enabled) return undefined;
    let cancelled = false;
    api.autopilot.listRuns({ limit: 8 })
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.runs ?? res?.runs ?? [];
        setRuns(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [runsOpen, enabled, state?.lastCycleStartedAt]);
  const visibleRuns = useMemo(() => (runs || [])
    .filter((r) => r.phase === 'plan' || r.status === 'failed'), [runs]);

  const applyEnabled = async (next) => {
    if (saving) return;
    setSaving(true);
    try {
      const tz = state?.timezone
        || Intl.DateTimeFormat().resolvedOptions().timeZone
        || 'UTC';
      const res = await api.autopilot.setState({ enabled: next, timezone: tz });
      const payload = res?.data?.state ?? res?.state ?? null;
      setState(payload);
      setError(null);
      // Lock the toggle briefly so it can't be flipped straight back — longer
      // when enabling, since that just kicked off a generation cycle.
      setCooldownUntil(Date.now() + (next ? ENABLE_COOLDOWN_MS : DISABLE_COOLDOWN_MS));
    } catch (err) {
      // TASK-331 (D6 fix follow-up): unlocking the read wall above (see
      // autopilotEntitled) exposes this toggle to a lapsed org for the first
      // time — enabling still correctly 402s PLAN_LAPSED server-side, so open
      // the renew modal instead of a plain inline error, matching every other
      // PLAN_LAPSED-first handler in the app.
      if (next && err?.code === 'PLAN_LAPSED') {
        openUpgrade({ feature: 'autopilot', variant: 'renew' });
      } else if (next && err?.code === 'AUTOPILOT_NO_SOCIAL_ACCOUNTS') {
        setConnectedCount(0);
        setError(t('autopilotWidget.connectSocialPrerequisite'));
      } else {
        setError(err?.message || t('autopilotWidget.failedToSave'));
      }
    } finally {
      setSaving(false);
    }
  };
  // Turning OFF is immediate. Turning ON first asks for explicit consent — the
  // confirm modal explains autopilot auto-generates + publishes — so the
  // warning appears ONLY on an enable attempt, never on its own.
  const inCooldown = cooldownUntil > Date.now();
  const toggleLocked = saving || inCooldown || (!state && !!error);
  const needsSocialAccount = !enabled && connectedCount === 0;
  const toggle = () => {
    if (toggleLocked) return;
    if (enabled) applyEnabled(false);
    else setShowEnableModal(true);
  };

  const noBrand = setupStatus?.brand === 'missing';

  // Plan gate comes before the no-brand guard: the guard previews an entitled
  // feature whose setup is incomplete; Free always gets the existing upsell.
  if (entitlementsLoaded && (!autopilotEntitled || awaitingFirstPayment)) {
    return (
      <section className="ap-card ap-card--locked">
        <div className="ap-decor" aria-hidden="true"><div className="ap-bg" /></div>
        <header className="ap-h">
          <div className="ap-h-l">
            <span className="ap-h-mark"><I.sparkles size={14} /></span>
            <div>
              <div className="eyebrow"><span>{t('autopilotWidget.autopilot')}</span></div>
              <h3 className="ap-title">{t(awaitingFirstPayment ? 'autopilotWidget.trialTitle' : 'autopilotWidget.lockedTitle')}</h3>
            </div>
          </div>
          <button type="button" className="ap-upgrade" onClick={() => awaitingFirstPayment ? navigate('/app/plans') : openUpgrade({ feature: 'autopilot' })}>
            <I.zap size={13} /> {t(awaitingFirstPayment ? 'autopilotWidget.activate' : 'autopilotWidget.upgrade')}
          </button>
        </header>
        <p className="ap-locked-desc">{t(awaitingFirstPayment ? 'autopilotWidget.trialDesc' : 'autopilotWidget.lockedDesc')}</p>
      </section>
    );
  }

  if (noBrand) {
    return (
      <section className="ap-card">
        <div className="ap-decor" aria-hidden="true"><div className="ap-bg" /></div>
        <header className="ap-h">
          <div className="ap-h-l">
            <span className="ap-h-mark"><I.sparkles size={14} /></span>
            <div>
              <div className="eyebrow"><span>{t('autopilotWidget.autopilot')}</span></div>
              <h3 className="ap-title">{t('autopilotWidget.pausedManualMode')}</h3>
            </div>
          </div>
          <button
            className="ap-toggle"
            disabled
            aria-describedby="no-brand-guard-hint-autopilot"
            title={t('noBrandGuard.disabledHint')}
            aria-label={t('autopilotWidget.toggleAutopilot')}
            type="button"
          >
            <span className="ap-toggle-knob" />
            <span className="ap-toggle-l">{t('autopilotWidget.off')}</span>
          </button>
        </header>
        <NoBrandGuard surface="autopilot" />
        <div className="ap-strip" aria-label={t('noBrandGuard.previewLabel')}>
          {AP_TASKS.map((taskItem) => {
            const TaskIcon = I[taskItem.icon] || I.sparkles;
            return (
              <div key={taskItem.id} className="ap-step ap-step-soon">
                <span className="ap-step-ic"><TaskIcon size={11} /></span>
                <div className="ap-step-text">
                  <div className="ap-step-l">{t(`autopilotWidget.tasks.${taskItem.id}.label`)}</div>
                  <div className="ap-step-s"><span className="mono">{t(`autopilotWidget.tasks.${taskItem.id}.day`)}</span></div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  // Skeleton — keep card shape stable on first paint.
  if (loading && !state) {
    return (
      <section className="ap-card">
        <div className="ap-bg" />
        <header className="ap-h">
          <div className="ap-h-l">
            <span className="ap-h-mark"><I.sparkles size={14} /></span>
            <div>
              <div className="eyebrow"><span>{t('autopilotWidget.autopilot')}</span></div>
              <h3 className="ap-title">{t('autopilotWidget.loading')}</h3>
            </div>
          </div>
        </header>
      </section>
    );
  }

  // When rendered on the dedicated /app/autopilot screen we always show the
  // full cycle (no collapse affordance); on the Dashboard it stays collapsible.
  const effectiveCollapsed = alwaysExpanded ? false : collapsed;

  const currentCycle = currentPlanCycle(state);
  const publishComplete = generationComplete && currentCycle?.published >= latestPlan.batch.totalItems
    && currentCycle.scheduled === 0 && currentCycle.failed === 0;
  const completedStep = (index) => enabled && ((index === 0 && generationComplete) || (index === 1 && publishComplete));
  const trackFillPct = enabled ? (publishComplete ? AP_TASKS[2].phase : generationComplete ? AP_TASKS[1].phase : 0) * 100 : 0;
  // Countdown to the next real generation (Sunday PLAN), so "in 4 d" can't be
  // mistaken for "nothing is happening" — it tells the user when posts come.
  // Server nextPlanAt is authoritative (computed in the user's saved timezone,
  // and correct on Sunday mornings); the client formula is the fallback.
  const nextBatchCountdown = formatCountdown(state?.nextPlanAt || nextPlanIsoFrom(new Date()), t);

  // Current-cycle publish outcomes come from the worker, independently of
  // the batch admission receipt and its generation rollup.
  // During staggered rollout an older backend may return the previous batch.
  // Keep that outcome explicitly historical and use its OWN date.
  const previousCycle = !Object.hasOwn(state || {}, 'lastPlanRun') && state?.lastCycle?.startedAt ? state.lastCycle : null;
  const lastCycle = currentCycle || previousCycle;
  const lastInsight = state?.lastInsight || null;
  const cycleText = cycleSummaryText(lastCycle, t);
  const cycleMoment = formatRunDate(lastCycle?.startedAt, i18n.language);
  const statusText = planStatus === 'idle' ? t('autopilotWidget.standingByIdle') : t(`autopilotWidget.runStatus.${planStatus}`);
  const activityText = cycleText || (planStatus !== 'idle' ? statusText : null);
  const nextRetry = latestPlan?.status === 'retrying' ? formatRunDate(latestPlan.nextRetryAt, i18n.language) : null;
  const nextPlanLabel = formatPlanDate(state?.nextPlanAt || nextPlanIsoFrom(new Date()), i18n.language, state?.timezone);
  const reconnectPlatform = (enabled && lastCycle?.needsReconnect?.length) ? lastCycle.needsReconnect[0] : null;
  const reconnectName = lastCycle?.reconnectName || null;

  return (
    <section className="ap-card">
      {/* Decorations (subtle gradient bg + animated brand orb) live in a
          dedicated clipping wrapper so the card itself can keep
          overflow:visible — that's what lets the tick tooltips escape
          the card's top edge cleanly instead of getting chopped at the
          rounded border. */}
      <div className="ap-decor" aria-hidden="true">
        <div className="ap-bg" />
        <div className="ap-orb" data-active={activelyWorking ? 'true' : 'false'} />
      </div>

      <header className="ap-h">
        <div className="ap-h-l">
          <span className="ap-h-mark" data-active={enabled ? 'true' : 'false'}>
            <I.sparkles size={14} />
          </span>
          <div>
            <div className="eyebrow"><span>{t('autopilotWidget.autopilot')}</span></div>
            <h3 className="ap-title">
              {error ? (
                <>{statusText}</>
              ) : enabled ? (
                !['idle', 'ready'].includes(planStatus) ? (
                  <>{statusText}</>
                ) : phaseRunning ? (
                  <>{t('autopilotWidget.running')} <span className="ap-title-ac">{t(`autopilotWidget.tasks.${task.id}.label`).toLowerCase()}</span></>
                ) : freshRun ? (
                  <>
                    {t('autopilotWidget.weekDoneTitle')}
                    {latestPlanMoment && <span className="ap-title-ac"> · {latestPlanMoment}</span>}
                  </>
                ) : (
                  <>{t('autopilotWidget.standingByIdle')}</>
                )
              ) : (
                <>{t('autopilotWidget.pausedManualMode')}</>
              )}
            </h3>
            {brand.enabled && brand.brands.length > 1 && (
              <div className="ap-brand-context">
                <div className="ap-brand-row">
                  <span className="ap-brand-tile">
                    {brand.activeBrand?.logoUrl
                      ? <img src={brand.activeBrand.logoUrl} alt="" />
                      : (brand.activeBrand?.monogram || brand.activeBrand?.name?.trim().charAt(0).toUpperCase() || 'B')}
                  </span>
                  <span>{brand.activeBrand?.name || t('sidebar.yourBrand')}</span>
                </div>
                <div className="ap-brand-note">{t('autopilotWidget.perBrandNote')}</div>
              </div>
            )}
          </div>
        </div>

        <div className="ap-h-r">
          {!needsSocialAccount && (
            <button
              className={`ap-toggle ${enabled ? 'is-on' : ''}`}
              onClick={toggle}
              disabled={toggleLocked}
              title={inCooldown ? t('autopilotWidget.toggleCooldown') : undefined}
              aria-label={t('autopilotWidget.toggleAutopilot')}
              type="button"
            >
              <span className="ap-toggle-knob" />
              <span className="ap-toggle-l">{saving ? '…' : (enabled ? t('autopilotWidget.on') : t('autopilotWidget.off'))}</span>
            </button>
          )}
          {!alwaysExpanded && (
            <button
              className="ap-expand"
              type="button"
              aria-label={collapsed ? t('autopilotWidget.showFullCycle') : t('autopilotWidget.collapseCycle')}
              aria-expanded={!collapsed}
              onClick={() => {
                setCollapsed((prev) => {
                  const next = !prev;
                  writeCollapsedPref(next);
                  return next;
                });
              }}
            >
              <I.chevDown size={14} className={collapsed ? '' : 'is-flipped'} />
            </button>
          )}
        </div>
      </header>

      {/* Progress track with phase tick marks. Fill requires completion
          evidence; ticks are placed at each task's `phase` value. Each tick
          carries a hover tooltip with the phase label/day/duration so
          users can see what each dot represents without expanding the
          full strip — that's the "glance" mode at the heart of the
          collapsed default. */}
      <div className={`ap-track ${effectiveCollapsed ? 'ap-track--collapsed' : ''}`}>
        <div className="ap-track-bg" />
        {AP_TASKS.map((s, i) => {
          const TaskIcon = I[s.icon] || I.sparkles;
          {/* When autopilot is OFF nothing is running — highlighting the
              day-of-week stage read as "it's doing something" (owner-reported:
              paused for weeks, yet Mon–Thu showed active). All neutral when off. */}
          const tickState = completedStep(i) ? 'done' : enabled && i === activeIdx ? 'active' : 'soon';
          return (
            <div
              key={s.id}
              className={`ap-tick ${enabled && i === activeIdx ? 'is-active' : ''} ${completedStep(i) ? 'is-done' : ''}`}
              style={{ left: `${s.phase * 100}%` }}
              tabIndex={0}
              role="button"
              aria-label={t('autopilotWidget.tickAriaLabel', { label: t(`autopilotWidget.tasks.${s.id}.label`), day: t(`autopilotWidget.tasks.${s.id}.day`), dur: t(`autopilotWidget.tasks.${s.id}.dur`) })}
            >
              <span className="ap-tick-dot" />
              <div className="ap-tick-tip" role="tooltip">
                <div className="ap-tick-tip-h">
                  <span className={`ap-tick-tip-ic ap-tick-tip-ic--${tickState}`}>
                    <TaskIcon size={11} />
                  </span>
                  <div className="ap-tick-tip-body">
                    <div className="ap-tick-tip-l">
                      {t(`autopilotWidget.tasks.${s.id}.label`)}
                      {s.soon && <span className="ap-soon">{t('autopilotWidget.comingSoon')}</span>}
                    </div>
                    <div className="ap-tick-tip-meta">
                      <span className="mono">{t(`autopilotWidget.tasks.${s.id}.day`)}</span>
                      <span className="ap-tick-tip-sep">·</span>
                      <span>{t(`autopilotWidget.tasks.${s.id}.dur`)}</span>
                    </div>
                  </div>
                </div>
                {s.detail && <div className="ap-tick-tip-detail">{t(`autopilotWidget.tasks.${s.id}.detail`)}</div>}
              </div>
            </div>
          );
        })}
        <div className="ap-fill" style={{ width: `${trackFillPct}%` }}>
          <span className="ap-fill-head" />
        </div>
      </div>

      {!effectiveCollapsed && (
        <>
          {/* The existing step strip animates only while work is confirmed active. */}
          <div className="ap-strip">
            {AP_TASKS.map((s, i) => {
              const TaskIcon = I[s.icon] || I.sparkles;
              const stepState = completedStep(i) ? 'done' : enabled && i === activeIdx ? 'now' : 'soon';
              return (
                <div key={s.id} className={`ap-step ap-step-${stepState}`}>
                  <span className="ap-step-ic">
                    {stepState === 'done' ? <I.check size={11} /> : <TaskIcon size={11} />}
                  </span>
                  <div className="ap-step-text">
                    <div className="ap-step-l">{t(`autopilotWidget.tasks.${s.id}.label`)}</div>
                    <div className="ap-step-s">
                      <span className="mono">{t(`autopilotWidget.tasks.${s.id}.day`)}</span>
                      <span className="ap-d-sep">·</span>
                      <span>{t(`autopilotWidget.tasks.${s.id}.dur`)}</span>
                      {/* The label row ellipsizes in narrow strip cards, which
                          would swallow an inline badge — the meta row never
                          truncates, so "soon" stays visible. */}
                      {s.soon && <span className="ap-soon">{t('autopilotWidget.comingSoon')}</span>}
                    </div>
                  </div>
                  {stepState === 'now' && activelyWorking && (
                    <div className="ap-step-progress">
                      <div className="ap-step-progress-fill" style={{ width: `${ambientT * 100}%` }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

        </>
      )}

      {/* Footer — the latest action as a clickable label (it toggles the
          recent activity feed below) + next-batch countdown, with a contextual
          action link on the right. ALWAYS visible (collapsed too):
          burying "Generated 6 posts" behind the expand chevron was exactly
          why autopilot read as "nothing ever happens". */}
      {/* "Last week" — the visible payoff of the Review phase: what the loop
          learned (top pillars · best time). Title shows the full AI note. */}
      {enabled && lastInsight && (lastInsight.topPillars?.length > 0 || lastInsight.bestTime) && (
        <div className="ap-insight" title={lastInsight.note || undefined}>
          <span className="ap-insight-k">{t('autopilotWidget.lastInsight')}</span>
          <b className="ap-insight-v">{[
            lastInsight.topPillars?.slice(0, 2).join(' & ') || null,
            lastInsight.bestTime ? `~${lastInsight.bestTime}` : null,
          ].filter(Boolean).join(' · ')}</b>
        </div>
      )}
      <footer className={`ap-foot ${effectiveCollapsed ? 'ap-foot--tight' : ''}`}>
        <span className="ap-foot-l">
          {error ? (
            <span style={{ color: 'var(--danger)' }}>{error}</span>
          ) : enabled ? (
            <>
              {activityText && (
                <>
                  <button
                    className="ap-foot-last"
                    type="button"
                    aria-expanded={runsOpen}
                    title={t('autopilotWidget.recentActivity')}
                    onClick={() => setRunsOpen((prev) => !prev)}
                  >
                    <b>{cycleText && previousCycle ? `${t('autopilotWidget.runStatus.previousCycle')} · ` : ''}{activityText}</b>
                    {(cycleText ? cycleMoment : latestPlanMoment) && <span className="mono">({cycleText ? cycleMoment : latestPlanMoment})</span>}
                    <I.chevDown size={11} className={runsOpen ? 'is-flipped' : ''} />
                  </button>
                  {lastCycle?.platforms?.length > 0 && (
                    <span className="ap-foot-plats">
                      {lastCycle.platforms.map((p) => (
                        <span key={p} className="ap-plat-chip">{platformLabel(p)}</span>
                      ))}
                    </span>
                  )}
                  {/* Mobile shell fix: wrapped so the separator can be hidden
                      at <=520px instead of leaving a dangling leading "·"
                      when the "next plan" segment drops to its own row. */}
                  <span className="ap-foot-sep"> · </span>
                </>
              )}
              {nextRetry ? (
                <>{t(planStatus === 'reconciling' ? 'autopilotWidget.runStatus.nextCheck' : 'autopilotWidget.runStatus.nextRetry')} <b>{nextRetry}</b></>
              ) : nextPlanLabel ? (
                <>{t('autopilotWidget.nextPlan')} <b>{nextPlanLabel}</b></>
              ) : (
                <>{t('autopilotWidget.nextBatch')} <b>{nextBatchCountdown}</b></>
              )}
            </>
          ) : needsSocialAccount ? (
            <>{t('autopilotWidget.connectSocialPrerequisite')}</>
          ) : (
            <>{t('autopilotWidget.turnOnHint')}</>
          )}
        </span>
        <Link to={needsSocialAccount ? '/app/settings?tab=accounts' : '/app/plan/edit-strategy'} className="ap-foot-link">
          {needsSocialAccount ? t('autopilotWidget.connectSocialAccount') : t('autopilotWidget.customizeCycle')} <I.arrowRight size={11} />
        </Link>
      </footer>

      {/* Reconnect prompt — when a channel was revoked, last week's post to it
          failed (the OTHER targets still went out). Surfacing it here turns a
          silent 'failed' into a one-click fix instead of an invisible dead end. */}
      {reconnectPlatform && (
        <div className="ap-reconnect">
          <span className="ap-reconnect-ic"><I.alert size={12} /></span>
          <span className="ap-reconnect-tx">
            {reconnectName
              ? t('autopilotWidget.reconnectHintNamed', { account: reconnectName, platform: platformLabel(reconnectPlatform) })
              : t('autopilotWidget.reconnectHint', { platform: platformLabel(reconnectPlatform) })}
          </span>
          <Link to="/app/settings?tab=accounts" className="ap-reconnect-cta">
            {t('autopilotWidget.reconnect')} <I.arrowRight size={11} />
          </Link>
        </div>
      )}

      {/* Recent activity — real runs from autopilot_runs, opened from the
          footer label. Available in collapsed mode too, so the history is
          one click away at any moment. */}
      {enabled && runsOpen && (
        <div className="ap-runs ap-runs--foot">
          {runs == null ? (
            <div className="ap-run"><span className="dot pulse" /></div>
          ) : visibleRuns.length === 0 ? (
            <div className="ap-run"><span className="ap-run-sum">—</span></div>
          ) : visibleRuns.map((r) => {
            const runStatus = autopilotActivityStatus(r);
            return (
            <div key={r.id} className={`ap-run ${r.status === 'failed' ? 'is-failed' : ''}`}>
              <span className="ap-run-ic">
                {runStatus === 'failed' ? <I.alert size={11} /> : <I.clock size={11} />}
              </span>
              <span className="ap-run-sum">
                {runStatus === 'accepted' && r.summary ? localizeRunSummary(r.summary, t) : t(`autopilotWidget.runStatus.${runStatus}`)}
              </span>
              <span className="ap-run-when mono">{formatRunDate(r.startedAt, i18n.language)}</span>
            </div>
            );
          })}
        </div>
      )}

      <AutopilotIntroModal
        open={showEnableModal}
        busy={saving}
        onConfirm={() => { setShowEnableModal(false); applyEnabled(true); }}
        onCustomize={() => { setShowEnableModal(false); navigate('/app/plan/edit-strategy'); }}
        onClose={() => setShowEnableModal(false)}
      />
    </section>
  );
}
